import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '../../DataDomain';
import {
    IContentLikeDislikePayload,
    IGetContentRatingResponse,
    IGetContentRatingSearchParams,
    ILearnActionResponse,
    ILearnEventPayload,
    ISearchClickActionPayload,
    ISetPreferencesActionPayload,
    ISetPreferencesActionResponse,
} from '../../entity/interface/iLearnAction';
import { IAddLikeDislikeUseCase } from '../../core/usecases/learnAction/AddLikeDislikeUseCase';
import { IGetContentRatingUseCase } from '../../core/usecases/learnAction/GetContentRatingUseCase';
import { ISearchClickActionUseCase } from '../../core/usecases/learnAction/SearchClickActionUseCase';
import { ILearnEventUseCase } from '../../core/usecases/learnAction/LearnEventUseCase';
import { ISetPreferencesActionUseCase } from '../../core/usecases/learnAction/SetPreferencesActionUseCase';

export interface ILearnActionService {
    addLikeDislike(apiKey: string, paylod: IContentLikeDislikePayload, identityToken: string): Promise<any>;
    getContentRating(
        apiKey: string,
        params: IGetContentRatingSearchParams,
        identityToken: string
    ): Promise<IGetContentRatingResponse['data']>;

    searchClickAction(
        apiKey: string,
        paylod: ISearchClickActionPayload,
        identityToken: string
    ): Promise<ILearnActionResponse>;

    learnEvent(apiKey: string, paylod: ILearnEventPayload, identityToken: string): Promise<ILearnActionResponse>;

    setPreferencesAction(
        apiKey: string,
        payload: ISetPreferencesActionPayload,
        identityToken: string,
        authToken: string,
        epochMs?: string,
        encPassword?: string
    ): Promise<ISetPreferencesActionResponse>;
}

export class LearnActionService implements ILearnActionService {
    // Lazy getters for dependencies to avoid circular dependencies
    private get addLikeDislikeUseCase(): IAddLikeDislikeUseCase {
        return container.resolve<IAddLikeDislikeUseCase>(DI.USECASES.IAddLikeDislikeUseCase);
    }

    private get getContentRatingUseCase(): IGetContentRatingUseCase {
        return container.resolve<IGetContentRatingUseCase>(DI.USECASES.IGetContentRatingUseCase);
    }

    private get searchClickActionUseCase(): ISearchClickActionUseCase {
        return container.resolve<ISearchClickActionUseCase>(DI.USECASES.ISearchClickActionUseCase);
    }

    private get learnEventUseCase(): ILearnEventUseCase {
        return container.resolve<ILearnEventUseCase>(DI.USECASES.ILearnEventUseCase);
    }

    private get setPreferencesActionUseCase(): ISetPreferencesActionUseCase {
        return container.resolve<ISetPreferencesActionUseCase>(DI.USECASES.ISetPreferencesActionUseCase);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async addLikeDislike(apiKey: string, paylod: IContentLikeDislikePayload, identityToken: string): Promise<boolean> {
        const response = await this.addLikeDislikeUseCase.execute(apiKey, paylod, identityToken);
        return response;
    }

    async getContentRating(
        apiKey: string,
        params: IGetContentRatingSearchParams,
        identityToken: string
    ): Promise<IGetContentRatingResponse['data']> {
        const response = await this.getContentRatingUseCase.execute(apiKey, params, identityToken);
        return response;
    }
    async searchClickAction(
        apiKey: string,
        paylod: ISearchClickActionPayload,
        identityToken: string
    ): Promise<ILearnActionResponse> {
        const response = await this.searchClickActionUseCase.execute(apiKey, paylod, identityToken);
        return response;
    }
    async learnEvent(apiKey: string, paylod: ILearnEventPayload, identityToken: string): Promise<ILearnActionResponse> {
        const response = await this.learnEventUseCase.execute(apiKey, paylod, identityToken);
        return response;
    }
    async setPreferencesAction(
        apiKey: string,
        payload: ISetPreferencesActionPayload,
        identityToken: string,
        authToken: string,
        epochMs?: string,
        encPassword?: string
    ): Promise<ISetPreferencesActionResponse> {
        const response = await this.setPreferencesActionUseCase.execute(
            apiKey,
            payload,
            identityToken,
            authToken,
            epochMs,
            encPassword
        );
        return response;
    }
}
